import { Button, Empty, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { DownloadOutlined, CodeOutlined } from '@ant-design/icons'
import { DataTable, Loader } from '@kolab/dms-base/components/ui'
import { useDeviceCommands } from '../../../hooks/useCommands'
import type { Command } from '../../../types/command.types'
import { formatDate } from '../../../utils/formatters'
import { exportToCsv } from '../../../utils/csvExport'

interface CommandLogViewProps {
  deviceId: string
}

const typeColorMap: Record<string, string> = {
  LOCK: 'red',
  UNLOCK: 'green',
  MESSAGE: 'blue',
}

const statusColorMap: Record<string, string> = {
  PENDING: 'orange',
  EXECUTED: 'green',
  RECEIVED: 'blue',
}

const resultColorMap: Record<string, string> = {
  SUCCESS: 'green',
  FAILURE: 'red',
}

const columns: ColumnsType<Command> = [
  {
    title: 'Date',
    dataIndex: 'createdAt',
    key: 'date',
    width: 200,
    sorter: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    defaultSortOrder: 'descend',
    render: (value: string) => (
      <span style={{ fontSize: 13, color: '#475569' }}>
        {formatDate(value)}
      </span>
    ),
  },
  {
    title: 'Type',
    dataIndex: 'type',
    key: 'type',
    width: 120,
    filters: [
      { text: 'Lock', value: 'LOCK' },
      { text: 'Unlock', value: 'UNLOCK' },
      { text: 'Message', value: 'MESSAGE' },
    ],
    onFilter: (value, record) => record.type === value,
    render: (type: string) => (
      <Tag color={typeColorMap[type] ?? 'default'} style={{ borderRadius: 6, fontSize: 12 }}>
        {type}
      </Tag>
    ),
  },
  {
    title: 'Created By',
    dataIndex: 'createdBy',
    key: 'createdBy',
    width: 130,
    render: (value: string) => (
      <span style={{ fontSize: 13, color: '#475569' }}>{value || '--'}</span>
    ),
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    width: 130,
    filters: [
      { text: 'Pending', value: 'PENDING' },
      { text: 'Executed', value: 'EXECUTED' },
      { text: 'Received', value: 'RECEIVED' },
    ],
    onFilter: (value, record) => record.status === value,
    render: (status: string) => (
      <Tag color={statusColorMap[status] ?? 'default'} style={{ borderRadius: 6, fontSize: 12 }}>
        {status}
      </Tag>
    ),
  },
  {
    title: 'Result',
    dataIndex: 'result',
    key: 'result',
    width: 120,
    render: (result: string | undefined) => {
      if (!result) return <span style={{ fontSize: 13, color: '#94a3b8' }}>{'--'}</span>
      return (
        <Tag color={resultColorMap[result] ?? 'default'} style={{ borderRadius: 6, fontSize: 12 }}>
          {result}
        </Tag>
      )
    },
  },
  {
    title: 'Error',
    dataIndex: 'errorMessage',
    key: 'errorMessage',
    width: 200,
    render: (value: string | undefined | null) => {
      if (!value) return <span style={{ fontSize: 13, color: '#94a3b8' }}>{'--'}</span>
      return (
        <span style={{ fontSize: 13, color: '#dc2626' }}>{value}</span>
      )
    },
  },
]

export default function CommandLogView({ deviceId }: CommandLogViewProps) {
  const { items, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useDeviceCommands(deviceId)

  const handleExportCsv = () => {
    const rows = items.map((c: Command) => ({
      Date: formatDate(c.createdAt),
      Type: c.type,
      'Created By': c.createdBy,
      Status: c.status,
      Result: c.result || '',
      Error: c.errorMessage || '',
      Message: c.message?.message || '',
    }))
    exportToCsv(`command-log-${deviceId}.csv`, rows)
  }

  if (isLoading) return <Loader />

  return (
    <div style={{ padding: '24px 16px', height: '100%', display: 'flex', flexDirection: 'column', background: '#f8fafc' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1e293b', margin: '0 0 8px' }}>
            Command Log
          </h1>
          <p style={{ fontSize: 14, color: '#64748b', margin: 0, maxWidth: 500 }}>
            Review commands sent to this device and their execution status.
          </p>
        </div>
        <Button
          icon={<DownloadOutlined />}
          onClick={handleExportCsv}
          disabled={items.length === 0}
          style={{ borderRadius: 8, fontWeight: 500, flexShrink: 0 }}
        >
          Export CSV
        </Button>
      </div>

      <DataTable<Command>
        columns={columns}
        dataSource={items}
        rowKey="id"
        infiniteScroll={{ isFetchingNextPage, hasNextPage, fetchNextPage }}
        scroll={{ x: 900 }}
        locale={{
          emptyText: (
            <Empty
              image={<CodeOutlined style={{ fontSize: 48, color: '#cbd5e1' }} />}
              description={
                <span style={{ color: '#94a3b8' }}>No commands recorded yet</span>
              }
            />
          ),
        }}
        style={{
          background: '#fff',
          borderRadius: 12,
          overflow: 'hidden',
        }}
      />
    </div>
  )
}
